import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import ReactSelect from 'react-select';
import {
    FaCheckCircle,
    FaEdit,
    FaSearch,
    FaSlidersH,
    FaTimes,
    FaSave,
    FaSyncAlt,
} from 'react-icons/fa';
import { rootUrl } from '../../../config/config';
import Alerty from '../../common/Alerty';
import { useAuth } from '../AuthContext';

const getFilteredSettings = (settings, searchTerm) => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return settings;

    return settings.filter((setting) =>
        setting.name.toLowerCase().includes(term) ||
        (setting.description && setting.description.toLowerCase().includes(term))
    );
};

const getSettingLabel = (setting) => {
    if (setting?.name === 'daily_summary_email_recipients') {
        return 'Daily Summary Email Recipients';
    }
    if (setting?.name === 'hourly_pending_messages_enabled') {
        return 'Hourly Pending Messages Report';
    }

    return setting?.name || 'Setting';
};

const isBooleanSetting = (setting) =>
    setting?.value === 'true' || setting?.value === 'false';

const SystemSettingsManager = () => {
    const { user, token } = useAuth();
    const authToken = token || localStorage.getItem('authToken') || 'default-token';

    const [settings, setSettings] = useState([]);
    const [cashbookAccounts, setCashbookAccounts] = useState([]);
    const [editStates, setEditStates] = useState({});
    const [loading, setLoading] = useState(true);
    const [savingId, setSavingId] = useState(null);
    const [alertOpen, setAlertOpen] = useState(false);
    const [alertColor, setAlertColor] = useState('blue');
    const [alertMsg, setAlertMsg] = useState('');
    const [searchTerm, setSearchTerm] = useState('');

    const fetchCashbookAccounts = async () => {
        try {
            const res = await axios.get(`${rootUrl}/api/cashbook/accounts`, {
                headers: { Authorization: `Bearer ${authToken}` },
                params: { is_active: 'true' },
            });
            const raw = res.data?.data || res.data?.accounts || (Array.isArray(res.data) ? res.data : []);
            setCashbookAccounts(raw);
        } catch {
            setCashbookAccounts([]);
        }
    };

    const fetchSettings = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${rootUrl}/system`, {
                headers: { Authorization: `Bearer ${authToken}` },
            });
            setSettings(Array.isArray(res.data) ? res.data : []);
        } catch (err) {
            setAlertColor('red');
            setAlertMsg('Failed to load system settings');
            setAlertOpen(true);
        } finally {
            setLoading(false);
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        fetchSettings();
        fetchCashbookAccounts();
    }, []);

    const handleValueChange = (id, value) => {
        setSettings((current) => current.map((setting) => (
            setting.id === id ? { ...setting, value } : setting
        )));
    };

    const handleEditClick = (id, value) => {
        setEditStates((prev) => ({
            ...prev,
            [id]: { editing: true, originalValue: value },
        }));
    };

    const handleCancelEdit = (id) => {
        setSettings((current) => current.map((setting) => (
            setting.id === id
                ? { ...setting, value: editStates[id]?.originalValue ?? setting.value }
                : setting
        )));

        setEditStates((prev) => ({
            ...prev,
            [id]: { editing: false, originalValue: prev[id]?.originalValue },
        }));
    };

    const handleSave = async (id, value, name, skipEditCheck = false) => {
        const originalValue = editStates[id]?.originalValue;

        if (!skipEditCheck && value === originalValue) {
            setAlertColor('blue');
            setAlertMsg('No changes to save');
            setAlertOpen(true);
            setEditStates((prev) => ({
                ...prev,
                [id]: { editing: false, originalValue },
            }));
            return;
        }

        setSavingId(id);
        try {
            await axios.put(
                `${rootUrl}/system/${name}`,
                {
                    value,
                    username: user?.username || 'system',
                },
                {
                    headers: { Authorization: `Bearer ${authToken}` },
                }
            );

            setAlertColor('green');
            setAlertMsg('Setting updated successfully');
            setAlertOpen(true);
            setEditStates((prev) => ({
                ...prev,
                [id]: { editing: false, originalValue: value },
            }));
        } catch (err) {
            setAlertColor('red');
            setAlertMsg('Failed to update setting');
            setAlertOpen(true);
        } finally {
            setSavingId(null);
        }
    };

    const filteredSettings = useMemo(
        () => getFilteredSettings(settings, searchTerm),
        [settings, searchTerm]
    );

    const editingCount = useMemo(
        () => Object.values(editStates).filter((state) => state?.editing).length,
        [editStates]
    );

    if (loading) {
        return (
            <div className="w-full max-w-6xl">
                <div className="rounded-xl border border-[#E7D6CA] bg-white px-6 py-12 text-center shadow-sm">
                    <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-[#FFF1EB] text-[#FF5733]">
                        <FaSyncAlt className="animate-spin text-lg" />
                    </div>
                    <p className="text-sm text-slate-600">Loading system settings...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full max-w-6xl space-y-6">
            <Alerty open={alertOpen} setOpen={setAlertOpen} color={alertColor} message={alertMsg} />

            <div className="overflow-hidden rounded-2xl border border-[#E7D6CA] bg-gradient-to-r from-[#fff8f4] via-[#fffdfb] to-[#f4eee8] shadow-sm">
                <div className="flex flex-col gap-6 px-6 py-7 lg:flex-row lg:items-end lg:justify-between">
                    <div className="max-w-2xl">
                        <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-[#FF5733] text-white shadow-sm">
                            <FaSlidersH className="text-lg" />
                        </div>
                        <h2 className="text-2xl font-bold text-[#2a2e30] sm:text-3xl">System Settings</h2>
                        <p className="mt-2 text-sm leading-6 text-slate-600 sm:text-base">
                            Review operational settings, search quickly, and update values inline without leaving the page.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                        <div className="rounded-xl border border-white/80 bg-white/80 px-4 py-3 backdrop-blur">
                            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Total</p>
                            <p className="mt-2 text-2xl font-bold text-[#2a2e30]">{settings.length}</p>
                        </div>
                        <div className="rounded-xl border border-white/80 bg-white/80 px-4 py-3 backdrop-blur">
                            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Visible</p>
                            <p className="mt-2 text-2xl font-bold text-[#2a2e30]">{filteredSettings.length}</p>
                        </div>
                        <div className="rounded-xl border border-white/80 bg-white/80 px-4 py-3 backdrop-blur">
                            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Editing</p>
                            <p className="mt-2 text-2xl font-bold text-[#2a2e30]">{editingCount}</p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="rounded-xl border border-[#E7D6CA] bg-white shadow-sm">
                <div className="border-b border-[#EFE3DB] px-6 py-5">
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                        <div>
                            <h3 className="text-lg font-bold text-[#2a2e30]">Settings Register</h3>
                            <p className="text-sm text-slate-500">
                                Search by setting name or description, then edit values directly in the list.
                            </p>
                        </div>

                        <div className="flex w-full flex-col gap-3 sm:flex-row lg:w-auto">
                            <div className="relative min-w-0 flex-1 lg:w-96">
                                <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400" />
                                <input
                                    type="text"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    placeholder="Search settings..."
                                    className="w-full rounded-lg border border-slate-300 bg-white py-2.5 pl-10 pr-4 text-sm text-slate-700 outline-none transition focus:border-[#FF5733] focus:ring-1 focus:ring-[#FF5733]"
                                />
                            </div>
                            <button
                                type="button"
                                onClick={fetchSettings}
                                disabled={loading}
                                className="inline-flex items-center justify-center gap-2 rounded-lg bg-slate-100 px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-200 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                                <FaSyncAlt className={loading ? 'animate-spin' : ''} />
                                Refresh
                            </button>
                        </div>
                    </div>
                </div>

                <div className="px-6 py-5">
                    {filteredSettings.length === 0 ? (
                        <div className="rounded-xl border border-dashed border-[#D7C3B5] bg-[#FFFaf7] px-6 py-12 text-center">
                            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-white text-slate-400 shadow-sm">
                                <FaSearch />
                            </div>
                            <p className="text-base font-semibold text-[#2a2e30]">No system settings found</p>
                            <p className="mt-2 text-sm text-slate-500">
                                Try a different search term or refresh the register.
                            </p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 gap-4">
                            {filteredSettings.map((setting) => {
                                const isEditing = !!editStates[setting.id]?.editing;
                                const isSaving = savingId === setting.id;

                                return (
                                    <div
                                        key={setting.id}
                                        className={`rounded-xl border px-5 py-5 shadow-sm transition ${
                                            isEditing
                                                ? 'border-[#FFBEAD] bg-[#FFF9F6]'
                                                : 'border-[#EFE3DB] bg-white hover:border-[#E2C8BA]'
                                        }`}
                                    >
                                        <div className="flex flex-col gap-5 xl:flex-row xl:items-center xl:justify-between">
                                            <div className="min-w-0 flex-1">
                                                <div className="flex flex-wrap items-center gap-3">
                                                    <h4 className="text-base font-bold text-[#2a2e30] break-words">{getSettingLabel(setting)}</h4>
                                                    {!isBooleanSetting(setting) && (
                                                        <span
                                                            className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${
                                                                isEditing
                                                                    ? 'bg-amber-100 text-amber-700'
                                                                    : 'bg-emerald-100 text-emerald-700'
                                                            }`}
                                                        >
                                                            {isEditing ? <FaEdit className="text-[10px]" /> : <FaCheckCircle className="text-[10px]" />}
                                                            {isEditing ? 'Editing' : 'Saved'}
                                                        </span>
                                                    )}
                                                </div>
                                                {setting.name === 'daily_summary_email_recipients' && (
                                                    <p className="mt-2 text-xs font-semibold uppercase tracking-[0.18em] text-[#FF5733]">
                                                        Configured as a comma-separated list
                                                    </p>
                                                )}
                                                <p className="mt-2 text-sm leading-6 text-slate-500">
                                                    {setting.description || 'No description provided for this setting.'}
                                                </p>
                                            </div>

                                            <div className="w-full xl:w-auto xl:min-w-[28rem]">
                                                {isBooleanSetting(setting) ? (
                                                    <div className="flex items-center gap-4">
                                                        <button
                                                            type="button"
                                                            role="switch"
                                                            aria-checked={setting.value === 'true'}
                                                            disabled={isSaving}
                                                            onClick={() => {
                                                                const newValue = setting.value === 'true' ? 'false' : 'true';
                                                                handleValueChange(setting.id, newValue);
                                                                handleSave(setting.id, newValue, setting.name, true);
                                                            }}
                                                            className={`relative inline-flex h-7 w-12 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none disabled:cursor-wait disabled:opacity-60 ${
                                                                setting.value === 'true' ? 'bg-[#FF5733]' : 'bg-slate-300'
                                                            }`}
                                                        >
                                                            <span
                                                                className={`inline-block h-6 w-6 transform rounded-full bg-white shadow transition duration-200 ${
                                                                    setting.value === 'true' ? 'translate-x-5' : 'translate-x-0'
                                                                }`}
                                                            />
                                                        </button>
                                                        <span className={`text-sm font-semibold ${setting.value === 'true' ? 'text-[#FF5733]' : 'text-slate-400'}`}>
                                                            {isSaving ? 'Saving...' : setting.value === 'true' ? 'Enabled' : 'Disabled'}
                                                        </span>
                                                    </div>
                                                ) : (
                                                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                                                        {setting.name === 'default_expense_cashbook_account_id' ? (
                                                            <div className="w-full">
                                                                <ReactSelect
                                                                    options={cashbookAccounts.map((a) => ({
                                                                        value: String(a.id),
                                                                        label: a.account_name ? `${a.account_name} (${a.account_type})` : String(a.id),
                                                                    }))}
                                                                    value={
                                                                        setting.value
                                                                            ? {
                                                                                value: String(setting.value),
                                                                                label: (() => {
                                                                                    const acct = cashbookAccounts.find((a) => String(a.id) === String(setting.value));
                                                                                    return acct ? `${acct.account_name} (${acct.account_type})` : `Account ${setting.value}`;
                                                                                })(),
                                                                            }
                                                                            : null
                                                                    }
                                                                    onChange={(opt) => handleValueChange(setting.id, opt ? opt.value : '')}
                                                                    isDisabled={!isEditing || isSaving}
                                                                    isClearable
                                                                    placeholder="Select default cashbook account…"
                                                                    menuPortalTarget={document.body}
                                                                    menuPosition="fixed"
                                                                />
                                                            </div>
                                                        ) : (
                                                            <input
                                                                type="text"
                                                                value={setting.value ?? ''}
                                                                onChange={(e) => handleValueChange(setting.id, e.target.value)}
                                                                disabled={!isEditing || isSaving}
                                                                className={`w-full rounded-lg border px-4 py-3 text-sm outline-none transition ${
                                                                    isEditing
                                                                        ? 'border-[#FFBEAD] bg-white text-slate-800 focus:border-[#FF5733] focus:ring-1 focus:ring-[#FF5733]'
                                                                        : 'border-slate-200 bg-slate-50 text-slate-500'
                                                                } ${isSaving ? 'cursor-wait opacity-70' : ''}`}
                                                            />
                                                        )}

                                                        {!isEditing ? (
                                                            <button
                                                                type="button"
                                                                onClick={() => handleEditClick(setting.id, setting.value)}
                                                                className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#FF5733] px-4 py-3 text-sm font-semibold text-white transition hover:bg-[#e34c28]"
                                                            >
                                                                <FaEdit className="text-xs" />
                                                                Edit
                                                            </button>
                                                        ) : (
                                                            <div className="flex flex-wrap gap-2">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleSave(setting.id, setting.value, setting.name)}
                                                                    disabled={isSaving}
                                                                    className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#FF5733] px-4 py-3 text-sm font-semibold text-white transition hover:bg-[#e34c28] disabled:cursor-not-allowed disabled:opacity-60"
                                                                >
                                                                    {isSaving ? <FaSyncAlt className="animate-spin text-xs" /> : <FaSave className="text-xs" />}
                                                                    {isSaving ? 'Saving...' : 'Save'}
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleCancelEdit(setting.id)}
                                                                    disabled={isSaving}
                                                                    className="inline-flex items-center justify-center gap-2 rounded-lg bg-slate-100 px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-200 disabled:cursor-not-allowed disabled:opacity-60"
                                                                >
                                                                    <FaTimes className="text-xs" />
                                                                    Cancel
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SystemSettingsManager;
